/**
 * LateCallWarningModal.jsx  ·  v3.21-1634
 *
 * P0 client-local-time late-call safeguard modal.  Rendered when
 * `useOutboundCall` receives a preflight response with
 * `warning_required=true`.  Two flavours:
 *
 *   • warning_type === "late_call"          → after 9:00 PM client-local
 *   • warning_type === "unknown_timezone"   → advisory (address missing)
 *
 * The rep must explicitly select "Proceed Anyway" — nothing else on
 * this dialog dials the number.  Cancel closes the dialog without
 * placing a call.
 */
import { AlertTriangle, Clock, MapPin, User, PhoneOff, PhoneForwarded } from 'lucide-react';
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';

const LateCallWarningModal = ({
  open,
  preflight,
  onCancel,
  onProceed,
  proceeding = false,
}) => {
  if (!preflight) return null;
  const unknown = preflight.warning_type === 'unknown_timezone';
  const title   = unknown ? 'Client Local Time Could Not Be Verified'
                          : 'Late Call Warning';
  const accent  = unknown ? 'amber' : 'red';
  const accentBar    = accent === 'red' ? 'bg-red-600' : 'bg-amber-500';
  const accentBg     = accent === 'red' ? 'bg-red-50'  : 'bg-amber-50';
  const accentBorder = accent === 'red' ? 'border-red-200' : 'border-amber-200';
  const accentText   = accent === 'red' ? 'text-red-700' : 'text-amber-800';
  const accentTitle  = accent === 'red' ? 'text-red-800' : 'text-amber-900';

  const locationLine = [preflight.client_city, preflight.state]
    .filter(Boolean).join(', ');

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden"
        data-testid="late-call-warning-modal"
      >
        {/* Prominent accent bar */}
        <div className={`h-1.5 w-full ${accentBar}`} />

        <div className="px-6 pt-5 pb-2 flex items-start gap-3">
          <div className={`p-2 rounded-full ${accentBg} ${accentBorder} border`}>
            <AlertTriangle className={`w-6 h-6 ${accentText}`} />
          </div>
          <div className="min-w-0">
            <DialogTitle
              className={`text-lg font-bold ${accentTitle}`}
              data-testid="late-call-warning-title"
            >
              {title}
            </DialogTitle>
            <DialogDescription className="sr-only">
              This warning appears when a call is initiated after 9:00 PM in
              the client&apos;s local time, or when the client&apos;s local time
              cannot be verified from stored address data.
            </DialogDescription>
          </div>
        </div>

        <div className="px-6 pb-5 space-y-4">
          {!unknown && (
            <div className={`rounded-md border ${accentBorder} ${accentBg} px-4 py-3 space-y-2`}>
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <Clock className={`w-4 h-4 ${accentText}`} />
                <span>
                  It is currently{' '}
                  <span className="font-semibold text-slate-900" data-testid="late-call-local-time">
                    {preflight.client_local_time}
                  </span>{' '}
                  in the client&apos;s local time
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600 pl-6">
                <span data-testid="late-call-tz-label">{preflight.timezone_label}</span>
              </div>
            </div>
          )}

          {unknown && (
            <div className={`rounded-md border ${accentBorder} ${accentBg} px-4 py-3 text-sm ${accentText}`}>
              We could not reliably determine the local time for this
              client.  Please verify that it is an appropriate time to
              call before proceeding.
            </div>
          )}

          <div className="bg-slate-50 border border-slate-200 rounded-md px-4 py-3 space-y-2 text-sm">
            {preflight.client_name && (
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-500" />
                <span className="font-medium text-slate-900" data-testid="late-call-client-name">
                  {preflight.client_name}
                </span>
              </div>
            )}
            {locationLine && (
              <div className="flex items-center gap-2 text-slate-700">
                <MapPin className="w-4 h-4 text-slate-500" />
                <span data-testid="late-call-location">{locationLine}</span>
              </div>
            )}
            {preflight.timezone_confidence && (
              <div className="text-xs text-slate-500 pl-6">
                Timezone source: {preflight.timezone_source} · confidence: {preflight.timezone_confidence}
              </div>
            )}
          </div>

          {!unknown && (
            <p className="text-sm text-slate-600">
              This client is currently outside the normal calling window.
              Are you sure you want to place this call?
            </p>
          )}

          <div className="flex flex-col sm:flex-row-reverse gap-2 pt-1">
            <Button
              variant={accent === 'red' ? 'destructive' : 'default'}
              className={
                accent === 'red'
                  ? 'w-full sm:w-auto'
                  : 'w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white'
              }
              onClick={onProceed}
              disabled={proceeding}
              data-testid="late-call-proceed-btn"
            >
              <PhoneForwarded className="w-4 h-4 mr-2" />
              Proceed Anyway
            </Button>
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={onCancel}
              disabled={proceeding}
              data-testid="late-call-cancel-btn"
            >
              <PhoneOff className="w-4 h-4 mr-2" />
              Cancel Call
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LateCallWarningModal;
